import { Skeleton } from '@crehana/compass.ui/skeleton';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';

import { useWidgetClockContext } from '../context-clock';
import { useTranslation } from '../i18n';
import type { TWidgetSize } from '../types';

type TTimerCircleProps = {
  // /**
  //  * The number of seconds that the user has worked within the schedule
  //  * after the entry mark (THIS MUST ONLY INCREASE WHEN THE USER HAS MARKED THE ENTRY)
  //  */
  // workedSeconds: number;
  // /**
  //  * The number of seconds that the user has worked outside of the schedule
  //  */
  // outOfScheduleSeconds?: number;
  // /**
  //  * The number of seconds that the user is expected to work
  //  */
  // expectedSeconds: number;
  size: TWidgetSize;
};

const SIZE_CONFIG = {
  s: { diameter: 112, strokeWidth: 8 },
  m: { diameter: 170, strokeWidth: 12 },
  l: { diameter: 180, strokeWidth: 12 },
} satisfies Record<TWidgetSize, { diameter: number; strokeWidth: number }>;

const formatTimer = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const TimerTooltipContent = ({
  workedSeconds,
  expectedSeconds,
  outOfScheduleSeconds,
}: {
  workedSeconds: number;
  expectedSeconds: number;
  outOfScheduleSeconds: number;
}) => {
  const { t } = useTranslation();

  return (
    <div className="tw-flex tw-flex-col tw-gap-0.4">
      <div className="tw-flex tw-items-center tw-gap-0.8">
        <span className="tw-inline-block tw-w-0.8 tw-h-0.8 tw-rounded-full tw-bg-[#4B22F4] tw-shrink-0" />
        <span>
          {t('timer_circle_time_registered')} {formatTimer(workedSeconds)}
        </span>
      </div>
      {expectedSeconds > 0 && (
        <div className="tw-flex tw-items-center tw-gap-0.8">
          <span className="tw-inline-block tw-w-0.8 tw-h-0.8 tw-rounded-full tw-cds-bg-disabled tw-shrink-0" />
          <span>
            {t('timer_circle_time_expected')} {formatTimer(expectedSeconds)}
          </span>
        </div>
      )}
      {outOfScheduleSeconds > 0 && (
        <div className="tw-flex tw-items-center tw-gap-0.8">
          <span className="tw-inline-block tw-w-0.8 tw-h-0.8 tw-rounded-full tw-bg-[#4589FF] tw-shrink-0" />
          <span>
            {t('timer_circle_hours_outside_schedule')}{' '}
            {formatTimer(outOfScheduleSeconds)}
          </span>
        </div>
      )}
    </div>
  );
};

export const TimerCircle = ({ size }: TTimerCircleProps) => {
  const { loading } = useWidgetClockContext();

  if (loading) {
    return <TimerCircleSkeleton size={size} />;
  }

  return <TimerCircleContent size={size} />;
};

/** SVG circular progress timer with dual arcs.
 * Uses stroke-dasharray/dashoffset technique: the full circumference is the "track",
 * and each arc's dashoffset controls how much of the stroke is visible.
 * The out-of-schedule arc starts where the main arc ends via a rotation transform. */
const TimerCircleContent = ({ size }: TTimerCircleProps) => {
  const { t } = useTranslation();
  const {
    timer: {
      workedSeconds,
      outOfScheduleSeconds,
      expectedSeconds,
      displaySeconds,
      hasShift,
    },
  } = useWidgetClockContext();

  const { diameter, strokeWidth } = SIZE_CONFIG[size];

  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Main arc: progress from 0 to 1 (capped), converted to SVG dashoffset.
  // dashoffset = circumference * (1 - progress) — full offset = invisible, zero = full circle.

  const mainProgress =
    expectedSeconds > 0 ? Math.min(workedSeconds / expectedSeconds, 1) : 0;
  const mainDashOffset = circumference * (1 - mainProgress);

  // Out-of-schedule arc: starts where the main arc ends.
  // Capped to remaining circle space (1 - mainProgress) to avoid overlap.
  const oosProgress =
    expectedSeconds > 0
      ? Math.min(outOfScheduleSeconds / expectedSeconds, 1 - mainProgress)
      : 0;
  const oosDashOffset = circumference * (1 - oosProgress);
  // Rotate so it starts at the main arc's end: -90° (SVG top) + main arc's angular extent.
  const oosRotation = -90 + mainProgress * 360;

  const center = diameter / 2;
  const showOos = hasShift && outOfScheduleSeconds > 0;

  const timerLabel =
    size === 's' ? 'tw-cds-font-body-01' : 'tw-cds-font-body-02';
  const timerValue =
    size === 's' ? 'tw-cds-font-subtitle-03' : 'tw-cds-font-heading-01';

  const strokeColorHex = '#EDEDED';

  return (
    <BasicTooltip
      content={
        <TimerTooltipContent
          workedSeconds={workedSeconds}
          expectedSeconds={expectedSeconds}
          outOfScheduleSeconds={outOfScheduleSeconds}
        />
      }
      side="top"
    >
      <div className="tw-relative tw-cursor-default">
        <svg
          width={diameter}
          height={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
        >
          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={strokeColorHex}
            strokeWidth={strokeWidth}
          />

          {/* Out-of-schedule arc */}
          {showOos && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#4589FF"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={oosDashOffset}
              strokeLinecap="round"
              transform={`rotate(${oosRotation} ${center} ${center})`}
              style={{
                transition: 'stroke-dashoffset 1s linear',
              }}
            />
          )}

          {/* Main progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#6556FF"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={mainDashOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            style={{
              transition: 'stroke-dashoffset 1s linear',
            }}
          />
        </svg>

        {/* Center text */}
        <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <span className={`${timerLabel} tw-cds-text-secondary`}>
            {t('timer_circle_t_registered')}
          </span>
          <span className={`${timerValue} tw-cds-text-primary`}>
            {formatTimer(displaySeconds)}
          </span>
        </div>
      </div>
    </BasicTooltip>
  );
};

const TimerCircleSkeleton = ({ size }: { size: TWidgetSize }) => {
  const { t } = useTranslation();

  const { diameter, strokeWidth } = SIZE_CONFIG[size];
  return (
    <Skeleton
      className="tw-rounded-full tw-cds-border-color-default tw-border-solid tw-flex tw-flex-col tw-items-center tw-justify-center"
      style={{
        width: diameter,
        height: diameter,
        borderWidth: strokeWidth,
        background: 'transparent',
      }}
    >
      <span className="tw-cds-font-body-01">
        {t('timer_circle_t_registered')}
      </span>
      <span
        className="tw-cds-font-heading-01 tw-cds-text-disabled"
        style={{
          background:
            'linear-gradient(90deg, #E3E4E5 44.06%, #FFF 59.69%, #E3E4E5 82.08%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        00:00
      </span>
    </Skeleton>
  );
};
